import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Container } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useApiCall, useApiDownload } from "../../useApiCall";
import Spinner from "../../Spinner";

const CourseViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const getSlides = useApiCall("/courses", "GET");
  const downloadSlide = useApiDownload("/course-slides", "GET");

  const [slideUrls, setSlideUrls] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [reachedEnd, setReachedEnd] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const abort = new AbortController();
    const createdUrls = [];

    setSlideUrls(null);
    setCurrentIndex(0);
    setReachedEnd(false);

    getSlides(undefined, `/${id}/slides`, { signal: abort.signal })
      .then((slides) => {
        if (cancelled || !Array.isArray(slides)) {
          return;
        }
        return Promise.all(
          slides.map((slide) =>
            downloadSlide(`/${slide.id}`)
              .then((resp) => resp.blob())
              .then((blob) => {
                const url = window.URL.createObjectURL(blob);
                createdUrls.push(url);
                return url;
              }),
          ),
        );
      })
      .then((urls) => {
        if (!cancelled && urls) {
          setSlideUrls(urls);
        }
      });

    return () => {
      cancelled = true;
      abort.abort();
      createdUrls.forEach((url) => window.URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const hasSlides = slideUrls && slideUrls.length > 0;

  useEffect(() => {
    if (hasSlides && currentIndex === slideUrls.length - 1) {
      setReachedEnd(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, hasSlides]);

  return (
    <Container fluid>
      <div className="page-header d-flex justify-content-between align-items-center">
        <h2>Προβολή μαθήματος</h2>
        <Button color="secondary" size="sm" onClick={() => navigate("/courses")}>
          <FontAwesomeIcon icon="arrow-left" /> Πίσω στη λίστα
        </Button>
      </div>

      {slideUrls === null && <Spinner />}

      {slideUrls !== null && !hasSlides && (
        <span className="text-muted">
          Δεν υπάρχουν διαθέσιμες διαφάνειες για αυτό το μάθημα&hellip;
        </span>
      )}

      {hasSlides && (
        <div className="text-center">
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "calc(100vh - 260px)",
              minHeight: "300px",
            }}
          >
            <img
              src={slideUrls[currentIndex]}
              alt={`Διαφάνεια ${currentIndex + 1}`}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                border: "1px solid #ddd",
              }}
            />
          </div>

          <div className="mt-3">
            <Button
              color="primary"
              disabled={currentIndex === 0}
              onClick={() => setCurrentIndex((i) => i - 1)}
            >
              <FontAwesomeIcon icon="arrow-left" /> Προηγούμενη
            </Button>{" "}
            <span className="mx-2">
              {currentIndex + 1} / {slideUrls.length}
            </span>{" "}
            <Button
              color="primary"
              size="sm"
              disabled={currentIndex === slideUrls.length - 1}
              onClick={() => setCurrentIndex((i) => i + 1)}
            >
              Επόμενη <FontAwesomeIcon icon="arrow-right" />
            </Button>
          </div>

          {reachedEnd && (
            <div className="mt-3">
              <Button
                color="success"
                onClick={() => navigate(`/courses/${id}/quiz`)}
              >
                <FontAwesomeIcon icon="arrow-right" /> Μετάβαση στο κουίζ
              </Button>
            </div>
          )}
        </div>
      )}
    </Container>
  );
};

export default CourseViewer;
